import { Button } from "@luciad/ria-sample-common/components/button/Button";
import { ButtonGroup } from "@luciad/ria-sample-common/components/ButtonGroup"
import { useMapContext } from "@luciad/ria-sample-common/hooks/useMapContext";
import { WebGLMap } from "@luciad/ria/view/WebGLMap";
import React from "react";
import { ConnectToHSPCWizard } from "./ConnectToHSPCWizard";
import { ConnectToLTSWizard } from "./ConnectToLTSWizard";
import { ConnectToOGC3DTilesWizard } from "./ConnectToOGC3DTilesWizard";
import { ConnectToOGCAPIMapsWizard } from "../ogcapimaps/ConnectToOGCAPIMapsWizard";
import { ConnectToOSMWizard } from "./ConnectToOSMWizard";
import { ConnectToWFSWizard } from "./ConnectToWFSWizard";
import { ConnectToWMSWizard } from "./ConnectToWMSWizard";
import { ConnectToWMTSWizard } from "./ConnectToWMTSWizard";
import { ModalPages, ModalWizard } from "./ModalWizard";

interface Props {
  show: boolean;
  onHide: () => void;
}

export const ConnectToModal = ({ show, onHide }: Props) => {
  return <ModalWizard show={show} onHide={onHide} startPageKey="chooseType" pages={PAGES} />;
}

type ConnectToPages =
  "chooseType"
  | "connectToOGCAPIMaps"
  | "connectToWMS"
  | "connectToWMTS"
  | "connectToWFS"
  | "connectToLTS"
  | "connectTo3DTiles"
  | "connectToHSPC"
  | "connectToOSM";

const PAGES: ModalPages<ConnectToPages> = {
  "chooseType": {
    title: "Connect to...",
    render: (changePage) => {
      const { map } = useMapContext();

      const isCanvasMap = !(map instanceof WebGLMap);

      return (
        <ButtonGroup vertical={true}>
          <Button title="Connect to a OGC API Maps service" onClick={() => changePage("connectToOGCAPIMaps")}>OGC API Maps</Button>
          <Button title="Connect to a WMS service" onClick={() => changePage("connectToWMS")}>WMS</Button>
          <Button title="Connect to a WMTS service" onClick={() => changePage("connectToWMTS")}>WMTS</Button>
          <Button title="Connect to a WFS service" onClick={() => changePage("connectToWFS")}>WFS</Button>
          <Button title="Connect to a LTS service" onClick={() => changePage("connectToLTS")}>LTS</Button>
          <Button title={isCanvasMap ? "You can only visualize OGC 3D Tiles data on a WebGLMap"
            : "Connect to a OGC 3D Tiles service"}
            disabled={isCanvasMap}
            onClick={() => changePage("connectTo3DTiles")}>
            3D tiles
          </Button>
          <Button title={isCanvasMap ? "You can only visualize HSPC data on a WebGLMap" : "Connect to a HSPC service"}
            disabled={isCanvasMap}
            onClick={() => changePage("connectToHSPC")}>
            HSPC
          </Button>
          <Button title="Connect to a WMS service" onClick={() => changePage("connectToOSM")}>OSM</Button>
        </ButtonGroup>
      );
    }
  },
  "connectToOGCAPIMaps": {
    title: "Connect to OGC API Maps",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToOGCAPIMapsWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectToWMS": {
    title: "Connect to WMS",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToWMSWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectToWMTS": {
    title: "Connect to WMTS",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToWMTSWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectToWFS": {
    title: "Connect to WFS",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToWFSWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectToLTS": {
    title: "Connect to LTS",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToLTSWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectTo3DTiles": {
    title: "Connect to 3D tiles service",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToOGC3DTilesWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectToHSPC": {
    title: "Connect to HSPC service",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToHSPCWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  },
  "connectToOSM": {
    title: "Connect to OSM service",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
        <ConnectToOSMWizard onPrevious={() => changePage(previousPageKey)} onReset={resetWizard} />
      );
    }
  }
}